import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/utils/products/constants';

export const useProductVariants = (product, setProduct, showNotification, activeVariantId) => {
    // Temp price state for editing
    const [tempPriceValue, setTempPriceValue] = useState("");
    const [tempPrediscountPriceValue, setTempPrediscountPriceValue] = useState("");
    
    // Editing price variant state
    const [editingPriceVariantId, setEditingPriceVariantId] = useState(null);
    const [editingPrediscountPriceVariantId, setEditingPrediscountPriceVariantId] = useState(null);
    
    // Update variant data function
    const handleVariantChange = async (variantId, field, value) => {
        try {
            // First update local state for immediate UI feedback
            setProduct(prev => ({
                ...prev,
                variants: prev.variants.map(variant => 
                    variant.id === variantId 
                        ? { 
                            ...variant, 
                            // Convert snake_case to camelCase for state
                            [field === 'isActive' ? 'is_active' : 
                             field === 'isLocked' ? 'is_locked' : 
                             field === 'prediscountPrice' ? 'prediscount_price' : field]: value 
                        } 
                        : variant
                )
            }));
            
            // Then update on the server
            // Only make API call for certain fields to avoid unnecessary requests
            if (['price', 'prediscountPrice', 'isActive', 'isLocked'].includes(field)) {
                const variant = product.variants.find(v => v.id === variantId);
                if (!variant) return;
                
                // Prepare update data according to API format
                const updateData = {
                    id: variantId,
                    price: field === 'price' ? value : variant.price,
                    prediscountPrice: field === 'prediscountPrice' ? value : variant.prediscount_price,
                    is_active: field === 'isActive' ? value : variant.is_active,
                    is_locked: field === 'isLocked' ? value : variant.is_locked
                };
                
                await axios.post(
                    `${API_BASE_URL}/product-variant/variant/update`,
                    updateData
                );
            }
        } catch (error) {
            console.error("Error updating variant:", error);
            showNotification("Failed to update variant: " + (error.message || "Unknown error"), "error");
            
            // Revert the change in local state if API call fails
            const variants = [...product.variants];
            const variantIndex = variants.findIndex(v => v.id === variantId);
            if (variantIndex !== -1) {
                setProduct(prev => ({
                    ...prev,
                    variants
                }));
            }
        }
    };
    
    // Handle entering price edit mode
    const handlePriceCellClick = (variant) => {
        setEditingPriceVariantId(variant.id);
        // Only set price value if there is an actual price, otherwise empty string
        setTempPriceValue(variant.price ? variant.price.toString() : "");
    };
    
    // Handle price change
    const handlePriceChange = (e) => {
        const value = e.target.value;
        // Allow empty string or valid numbers (can include decimals)
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setTempPriceValue(value);
        }
    };
    
    // Handle entering prediscount price edit mode
    const handlePrediscountPriceCellClick = (variant) => {
        setEditingPrediscountPriceVariantId(variant.id);
        // Only set prediscount price value if there is an actual price, otherwise empty string
        setTempPrediscountPriceValue(variant.prediscount_price ? variant.prediscount_price.toString() : "");
    };
    
    // Handle prediscount price change
    const handlePrediscountPriceChange = (e) => {
        const value = e.target.value;
        // Allow empty string or valid numbers (can include decimals)
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setTempPrediscountPriceValue(value);
        }
    };
    
    // Handle price update on blur or enter key
    const handlePriceUpdate = async (variantId) => {
        try {
            // Get the current variant
            const variant = product.variants.find(v => v.id === variantId);
            if (!variant) return;

            // Show loading toast
            showNotification("Đang cập nhật biến thể...", "info");
            
            // Auto set is_active based on price value
            const newPrice = tempPriceValue ? parseFloat(tempPriceValue) : null;
            const newIsActive = newPrice > 0 ? true : variant.is_active;
            
            // Create update data with both price and active status
            const updateData = {
                id: variantId,
                price: newPrice,
                prediscountPrice: variant.prediscount_price,
                is_active: newIsActive,
                is_locked: variant.is_locked
            };
            
            // Send API request to update variant
            const response = await axios.post(
                `${API_BASE_URL}/product-variant/variant/update`,
                updateData
            );
            
            if (response.data.isSuccess) {
                // Update local state directly from the response data
                // instead of calling handleVariantChange which may trigger another API call
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(v => 
                        v.id === variantId 
                            ? {
                                ...v,
                                price: newPrice,
                                is_active: newIsActive
                            }
                            : v
                    )
                }));
                
                // Show success toast
                showNotification("Cập nhật biến thể thành công", "success");
            } else {
                throw new Error(response.data.message || "Không thể cập nhật giá");
            }
        } catch (error) {
            console.error("Error updating price:", error);
            showNotification("Không thể cập nhật biến thể: " + (error.message || "Lỗi không xác định"), "error");
        } finally {
            // Exit edit mode regardless of success/failure
            setEditingPriceVariantId(null);
            setTempPriceValue('');
        }
    };
    
    // Handle prediscount price update on blur or enter key
    const handlePrediscountPriceUpdate = async (variantId) => {
        try {
            // Get the current variant
            const variant = product.variants.find(v => v.id === variantId);
            if (!variant) return;

            // Show loading toast
            showNotification("Đang cập nhật giá gốc...", "info");
            
            // Parse the new prediscount price value
            const newPrediscountPrice = tempPrediscountPriceValue ? parseFloat(tempPrediscountPriceValue) : null;
            
            // Create update data with prediscount price but maintain existing values for other fields
            const updateData = {
                id: variantId,
                price: variant.price,
                prediscountPrice: newPrediscountPrice,
                is_active: variant.is_active,
                is_locked: variant.is_locked
            };
            
            // Send API request to update variant
            const response = await axios.post(
                `${API_BASE_URL}/product-variant/variant/update`,
                updateData
            );
            
            if (response.data.isSuccess) {
                // Update local state directly from the response data
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(v => 
                        v.id === variantId 
                            ? {
                                ...v,
                                prediscount_price: newPrediscountPrice
                            }
                            : v
                    )
                }));
                
                // Show success toast
                showNotification("Cập nhật giá gốc thành công", "success");
            } else {
                throw new Error(response.data.message || "Không thể cập nhật giá gốc");
            }
        } catch (error) {
            console.error("Error updating prediscount price:", error);
            showNotification("Không thể cập nhật giá gốc: " + (error.message || "Lỗi không xác định"), "error");
        } finally {
            // Exit edit mode regardless of success/failure
            setEditingPrediscountPriceVariantId(null);
            setTempPrediscountPriceValue('');
        }
    };
    
    // Handle key press in price input
    const handlePriceKeyPress = (e, variantId) => {
        if (e.key === 'Enter') {
            handlePriceUpdate(variantId);
        } else if (e.key === 'Escape') {
            setEditingPriceVariantId(null);
            setTempPriceValue('');
        }
    };
    
    // Handle key press in prediscount price input
    const handlePrediscountPriceKeyPress = (e, variantId) => {
        if (e.key === 'Enter') {
            handlePrediscountPriceUpdate(variantId);
        } else if (e.key === 'Escape') {
            setEditingPrediscountPriceVariantId(null);
            setTempPrediscountPriceValue('');
        }
    };
    
    // Update variant active state with validation
    const handleActiveChange = async (variantId, checked) => {
        try {
            // Get the current variant
            const variant = product.variants.find(v => v.id === variantId);
            if (!variant) return;

            // Show loading toast
            showNotification("Đang cập nhật trạng thái...", "info");
            
            const response = await axios.post(
                `${API_BASE_URL}/product-variant/variant/update`,
                {
                    id: variantId,
                    price: variant.price,
                    prediscountPrice: variant.prediscount_price,
                    is_active: checked,
                    is_locked: variant.is_locked
                }
            );
            
            if (response.data.isSuccess) {
                // Update local state directly
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(v => 
                        v.id === variantId 
                            ? {
                                ...v,
                                is_active: checked
                            }
                            : v
                    )
                }));
                
                showNotification("Cập nhật trạng thái thành công", "success");
            } else {
                throw new Error(response.data.message || "Không thể cập nhật trạng thái");
            }
        } catch (error) {
            console.error("Error updating variant status:", error);
            showNotification("Không thể cập nhật trạng thái: " + (error.message || "Lỗi không xác định"), "error");
        }
    };
    
    // Handle lock change with validation
    const handleLockChange = async (variantId, checked) => {
        try {
            // Get the current variant
            const variant = product.variants.find(v => v.id === variantId);
            if (!variant) return;

            // Show loading toast
            showNotification("Đang cập nhật trạng thái khóa...", "info");
            
            const response = await axios.post(
                `${API_BASE_URL}/product-variant/variant/update`,
                {
                    id: variantId,
                    price: variant.price,
                    prediscountPrice: variant.prediscount_price,
                    is_active: variant.is_active,
                    is_locked: checked
                }
            );
            
            if (response.data.isSuccess) {
                // Update local state directly
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(v => 
                        v.id === variantId 
                            ? {
                                ...v,
                                is_locked: checked
                            }
                            : v
                    )
                }));
                
                showNotification("Cập nhật trạng thái khóa thành công", "success");
            } else {
                throw new Error(response.data.message || "Không thể cập nhật trạng thái khóa");
            }
        } catch (error) {
            console.error("Error updating variant lock status:", error);
            showNotification("Không thể cập nhật trạng thái khóa: " + (error.message || "Lỗi không xác định"), "error");
        }
    };
    
    // Handle media list reordering with drag and drop
    const handleMediaOrderChange = (variantId, result) => {
        if (!result.destination) return;
        
        const variant = product.variants.find(v => v.id === variantId);
        if (!variant) return;
        
        // Handle different data structures
        if (variant.mediaIds && Array.isArray(variant.mediaIds)) {
            // Old structure with mediaIds array
            const reorderedIds = Array.from(variant.mediaIds);
            const [movedItem] = reorderedIds.splice(result.source.index, 1);
            reorderedIds.splice(result.destination.index, 0, movedItem);
            
            // Update local state immediately for responsive UI
            setProduct(prev => ({
                ...prev,
                variants: prev.variants.map(v => 
                    v.id === variantId 
                        ? {
                            ...v,
                            mediaIds: reorderedIds
                        }
                        : v
                )
            }));
        } else if (variant.media && Array.isArray(variant.media)) {
            // New API structure with media array
            const reorderedMedia = Array.from(variant.media);
            const [movedItem] = reorderedMedia.splice(result.source.index, 1);
            reorderedMedia.splice(result.destination.index, 0, movedItem);
            
            // Update local state immediately for responsive UI
            setProduct(prev => ({
                ...prev,
                variants: prev.variants.map(v => 
                    v.id === variantId 
                        ? {
                            ...v,
                            media: reorderedMedia
                        }
                        : v
                )
            }));
        }
        
        // Not making direct API call here anymore - this will be handled by ProductVariants component
    };
    
    // Add media to variant (or remove if already added - toggle behavior)
    const handleSelectMediaForVariant = async (mediaId) => {
        if (!activeVariantId) return;
        
        try {
            // Find the current variant
            const variant = product.variants.find(v => v.id === activeVariantId);
            if (!variant) return;
            
            // Check if the media is already linked to the variant
            const isAlreadyLinked = variant.mediaIds && variant.mediaIds.includes(mediaId);
            
            if (isAlreadyLinked) {
                // If already linked, remove it (unlink)
                
                // First update local state for immediate feedback
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(v => {
                        if (v.id === activeVariantId) {
                            const updatedVariant = { ...v };
                            
                            // Remove from mediaIds array
                            if (updatedVariant.mediaIds) {
                                updatedVariant.mediaIds = updatedVariant.mediaIds.filter(
                                    id => id !== mediaId
                                );
                            }
                            
                            // Clear main mediaId if it matches
                            if (updatedVariant.mediaId === mediaId) {
                                updatedVariant.mediaId = updatedVariant.mediaIds && updatedVariant.mediaIds.length > 0 
                                    ? updatedVariant.mediaIds[0] 
                                    : null;
                            }
                            
                            return updatedVariant;
                        }
                        return v;
                    })
                }));
                
                // Then call API to unlink media from variant
                // Using the correct API endpoint from ProductApiUsage.md
                await axios.post(
                    `${API_BASE_URL}/product-media/${mediaId}/unlink-variant/${activeVariantId}`
                );
                
                showNotification("Media unlinked from variant", "success");
            } else {
                // If not already linked, add it (link)
                
                // First update local state for immediate feedback
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(v => {
                        if (v.id === activeVariantId) {
                            // Create a copy of the variant
                            const updatedVariant = { ...v };
                            
                            // Initialize mediaIds array if it doesn't exist
                            if (!updatedVariant.mediaIds) {
                                updatedVariant.mediaIds = [];
                            }
                            
                            // Add mediaId if not already in the array
                            if (!updatedVariant.mediaIds.includes(mediaId)) {
                                updatedVariant.mediaIds = [...updatedVariant.mediaIds, mediaId];
                            }
                            
                            // Set as main media if none is set
                            if (!updatedVariant.mediaId) {
                                updatedVariant.mediaId = mediaId;
                            }
                            
                            return updatedVariant;
                        }
                        return v;
                    })
                }));
                
                // Then call API to link media to variant
                // Using the correct API endpoint from ProductApiUsage.md
                await axios.post(
                    `${API_BASE_URL}/product-media/${mediaId}/link-variant/${activeVariantId}`
                );
                
                showNotification("Media linked to variant", "success");
            }
        } catch (error) {
            console.error("Error toggling media for variant:", error);
            showNotification("Failed to update variant media", "error");
        }
    };
    
    // Remove media from variant
    const handleRemoveMediaFromVariant = async (variantId, mediaIdToRemove) => {
        try {
            // First update local state for immediate feedback
            setProduct(prev => ({
                ...prev,
                variants: prev.variants.map(variant => {
                    if (variant.id === variantId) {
                        const updatedVariant = { ...variant };
                        
                        // Remove from mediaIds array
                        if (updatedVariant.mediaIds) {
                            updatedVariant.mediaIds = updatedVariant.mediaIds.filter(
                                id => id !== mediaIdToRemove
                            );
                        }
                        
                        // Clear main mediaId if it matches
                        if (updatedVariant.mediaId === mediaIdToRemove) {
                            updatedVariant.mediaId = updatedVariant.mediaIds && updatedVariant.mediaIds.length > 0 
                                ? updatedVariant.mediaIds[0] 
                                : '';
                        }
                        
                        return updatedVariant;
                    }
                    return variant;
                })
            }));
            
            // Then call API to unlink media from variant
            // Using the correct API endpoint from ProductApiUsage.md
            await axios.post(
                `${API_BASE_URL}/product-media/${mediaIdToRemove}/unlink-variant/${variantId}`
            );
            
            showNotification("Media removed from variant", "success");
        } catch (error) {
            console.error("Error removing media from variant:", error);
            showNotification("Failed to remove media from variant", "error");
        }
    };
    
    // Set media as main display image for variant
    const handleSetMainMedia = (variantId, mediaId) => {
        handleVariantChange(variantId, 'mediaId', mediaId);
        showNotification("Media set as main display image", "success");
    };
    
    // Validate product variants before saving
    const validateVariants = () => {
        let isValid = true;
        let errorMessage = "";
        
        for (const variant of product.variants) {
            // Active variants must have a price
            if (variant.is_active && !variant.price) {
                isValid = false;
                errorMessage = "Active variants must have a price";
                break;
            }
            
            // Variants with price must be active
            if (variant.price && !variant.is_active && !variant.is_locked) {
                isValid = false;
                errorMessage = "Variants with price must be active";
                break;
            }
            
            // Variants without price must be locked or inactive
            if (!variant.price && !variant.is_locked && variant.is_active) {
                isValid = false;
                errorMessage = "Variants without price must be inactive or locked";
                break;
            }
        }
        
        return { isValid, errorMessage };
    };
    
    return {
        handleVariantChange,
        handlePriceCellClick,
        handlePriceChange,
        handlePriceUpdate,
        handlePriceKeyPress,
        handlePrediscountPriceCellClick,
        handlePrediscountPriceChange,
        handlePrediscountPriceUpdate,
        handlePrediscountPriceKeyPress,
        handleActiveChange,
        handleLockChange,
        handleMediaOrderChange,
        handleSelectMediaForVariant,
        handleRemoveMediaFromVariant,
        handleSetMainMedia,
        validateVariants,
        tempPriceValue,
        editingPriceVariantId,
        tempPrediscountPriceValue,
        editingPrediscountPriceVariantId
    };
};